/**
 * BrainPipelineProgress - Real-time visualization of jelly-bop-brain processing
 *
 * Shows:
 * - Connection status
 * - Three-brain progress (MusicAnalyst, VisualDesigner, Screenwriter)
 * - Artist matches as they arrive
 * - Visual style preview
 * - Scene previews
 * - Final production brief status
 */

import { Brain, Music, Palette, Film, Check, Loader2, AlertCircle, User, Sparkles } from 'lucide-react';
import type {
  BrainPipelineState,
  BrainStatus,
  ArtistMatch,
} from '../../hooks/useBrainPipeline';

interface BrainStatuses {
  musicAnalyst: BrainStatus;
  visualDesigner: BrainStatus;
  screenwriter: BrainStatus;
}

interface BrainPipelineProgressProps {
  state: BrainPipelineState;
  brainStatuses: BrainStatuses;
  currentBrain: string | null;
  progress: number;
  error: string | null;
  artistMatches: ArtistMatch[];
  visualStylePreview: Record<string, unknown> | null;
  scenePreview: Record<string, unknown> | null;
  elapsedSeconds: number;
  onConfirmArtists?: (confirmed: boolean) => void;
  onAdjustStyle?: () => void;
}

// Brain display config
const BRAIN_CONFIG = {
  musicAnalyst: {
    label: 'Music Analyst',
    description: 'Finding similar artists & visual inferences',
    icon: Music,
    color: 'text-purple-400',
    bgColor: 'bg-purple-500/10',
    borderColor: 'border-purple-500/20',
  },
  visualDesigner: {
    label: 'Visual Designer',
    description: 'Creating color palette & persona style',
    icon: Palette,
    color: 'text-pink-400',
    bgColor: 'bg-pink-500/10',
    borderColor: 'border-pink-500/20',
  },
  screenwriter: {
    label: 'Screenwriter',
    description: 'Composing scene-by-scene breakdown',
    icon: Film,
    color: 'text-amber-400',
    bgColor: 'bg-amber-500/10',
    borderColor: 'border-amber-500/20',
  },
};

function BrainStatusIndicator({ status, brainKey }: { status: BrainStatus; brainKey: keyof typeof BRAIN_CONFIG }) {
  const config = BRAIN_CONFIG[brainKey];
  const Icon = config.icon;

  return (
    <div className={`flex items-center gap-3 p-3 rounded-lg ${config.bgColor} border ${config.borderColor}`}>
      <div className={`p-2 rounded-lg ${config.bgColor}`}>
        <Icon className={`w-5 h-5 ${config.color}`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-white text-sm">{config.label}</span>
          {status === 'processing' && (
            <Loader2 className="w-3 h-3 text-white/60 animate-spin" />
          )}
          {status === 'complete' && (
            <Check className="w-3 h-3 text-green-400" />
          )}
          {status === 'error' && (
            <AlertCircle className="w-3 h-3 text-red-400" />
          )}
        </div>
        <span className="text-xs text-white/50">{config.description}</span>
      </div>
    </div>
  );
}

function ArtistMatchCard({ match, index: _index }: { match: ArtistMatch; index: number }) {
  const scorePercent = Math.round(match.similarityScore * 100);
  const scoreColor = scorePercent >= 80 ? 'text-green-400' : scorePercent >= 60 ? 'text-amber-400' : 'text-white/60';

  return (
    <div className="flex items-start gap-3 p-3 bg-white/5 rounded-lg border border-white/10 animate-fade-in">
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
        <User className="w-4 h-4 text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-white">{match.artistName}</span>
          <span className={`text-sm font-medium ${scoreColor}`}>
            {scorePercent}%
          </span>
        </div>
        <p className="text-xs text-white/60 mt-1 line-clamp-2">{match.reasoning}</p>
        {match.visualStyleKeywords.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {match.visualStyleKeywords.slice(0, 4).map((keyword, i) => (
              <span
                key={i}
                className="px-2 py-0.5 text-xs bg-white/10 text-white/70 rounded-full"
              >
                {keyword}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function VisualStylePreview({ preview }: { preview: Record<string, unknown> }) {
  const colorPalette = (preview.color_palette || []) as string[];
  const personaDescription = (preview.persona_description || '') as string;
  const lightingApproach = (preview.lighting_approach || '') as string;

  return (
    <div className="p-4 bg-white/5 rounded-lg border border-white/10 animate-fade-in">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-4 h-4 text-pink-400" />
        <span className="font-medium text-white text-sm">Visual Style Preview</span>
      </div>

      {/* Color palette */}
      {colorPalette.length > 0 && (
        <div className="flex gap-2 mb-3">
          {colorPalette.slice(0, 5).map((color, i) => (
            <div
              key={i}
              className="w-8 h-8 rounded-lg border border-white/20"
              style={{ backgroundColor: color }}
              title={color}
            />
          ))}
        </div>
      )}

      {/* Persona description */}
      {personaDescription && (
        <p className="text-xs text-white/60 mb-2 line-clamp-3">
          {personaDescription}
        </p>
      )}

      {/* Lighting */}
      {lightingApproach && (
        <div className="flex items-center gap-2">
          <span className="text-xs text-white/40">Lighting:</span>
          <span className="text-xs text-white/70">{lightingApproach}</span>
        </div>
      )}
    </div>
  );
}

export default function BrainPipelineProgress({
  state,
  brainStatuses,
  currentBrain,
  progress,
  error,
  artistMatches,
  visualStylePreview,
  elapsedSeconds,
  onConfirmArtists,
  onAdjustStyle,
}: BrainPipelineProgressProps) {
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Don't render if idle
  if (state === 'idle') {
    return null;
  }

  return (
    <div className="p-4 bg-gradient-to-br from-purple-900/20 to-pink-900/20 rounded-xl border border-purple-500/20">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500">
            <Brain className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-white">AI Production Brief</h3>
            <p className="text-xs text-white/50">
              {state === 'connecting' && 'Connecting to brain...'}
              {state === 'streaming' && 'Receiving analysis data...'}
              {state === 'processing' && `Processing: ${currentBrain?.replace('_', ' ')}`}
              {state === 'complete' && 'Production brief ready!'}
              {state === 'error' && 'Error occurred'}
            </p>
          </div>
        </div>
        <div className="text-right">
          <div className="text-sm font-medium text-white/70">{formatTime(elapsedSeconds)}</div>
          {state === 'processing' && (
            <div className="text-xs text-white/40">{progress}%</div>
          )}
        </div>
      </div>

      {/* Error display */}
      {error && (
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
          <span className="text-sm text-red-400">{error}</span>
        </div>
      )}

      {/* Progress bar */}
      {(state === 'connecting' || state === 'streaming' || state === 'processing') && (
        <div className="mb-4">
          <div className="h-2 bg-white/10 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-500"
              style={{
                width: state === 'connecting' ? '10%' :
                       state === 'streaming' ? '25%' :
                       `${Math.max(30, progress)}%`
              }}
            />
          </div>
        </div>
      )}

      {/* Brain status grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <BrainStatusIndicator status={brainStatuses.musicAnalyst} brainKey="musicAnalyst" />
        <BrainStatusIndicator status={brainStatuses.visualDesigner} brainKey="visualDesigner" />
        <BrainStatusIndicator status={brainStatuses.screenwriter} brainKey="screenwriter" />
      </div>

      {/* Artist matches section */}
      {artistMatches.length > 0 && (
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-white/70">Similar Artists Found</h4>
            {onConfirmArtists && brainStatuses.musicAnalyst === 'complete' && (
              <button
                onClick={() => onConfirmArtists(true)}
                className="text-xs text-purple-400 hover:text-purple-300 transition-colors"
              >
                Confirm selection
              </button>
            )}
          </div>
          <div className="space-y-2">
            {artistMatches.slice(0, 3).map((match, i) => (
              <ArtistMatchCard key={i} match={match} index={i} />
            ))}
          </div>
        </div>
      )}

      {/* Visual style preview */}
      {visualStylePreview && (
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-white/70">Visual Style</h4>
            {onAdjustStyle && brainStatuses.visualDesigner === 'complete' && (
              <button
                onClick={onAdjustStyle}
                className="text-xs text-pink-400 hover:text-pink-300 transition-colors"
              >
                Adjust style
              </button>
            )}
          </div>
          <VisualStylePreview preview={visualStylePreview} />
        </div>
      )}

      {/* Complete state */}
      {state === 'complete' && (
        <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-lg flex items-center gap-3">
          <div className="p-2 rounded-full bg-green-500/20">
            <Check className="w-5 h-5 text-green-400" />
          </div>
          <div>
            <span className="font-medium text-green-400">Production Brief Ready</span>
            <p className="text-xs text-green-400/70">
              All brains have finished processing. Ready for video generation.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
